// src/chatGPT/components/LaborMarketTable.jsx
import React, { useState, useMemo, useContext } from 'react'
import { Copy, Download, Check } from 'lucide-react'
import {
  PiBriefcaseLight,
  PiCurrencyDollarLight,
  PiLaptopLight,
  PiRulerLight,
  PiAtomLight,
  PiHandshakeLight,
  PiScalesLight,
  PiBookOpenLight,
  PiMaskHappyLight,
  PiStethoscopeLight,
  PiHeartbeatLight,
  PiShieldCheckLight,
  PiChefHatLight,
  PiBroomLight,
  PiHandHeartLight,
  PiShoppingCartLight,
  PiOfficeChairLight,
  PiPlantLight,
  PiHardHatLight,
  PiWrenchLight,
  PiFactoryLight,
  PiTruckLight,
  PiTargetLight,
} from 'react-icons/pi'
import CircularRating from '../../components/CircularRating'
import DemandHoverTooltip from '../../components/DemandHoverTooltip'
import { ProductsContext } from '../../context/products.context'

// Icon mapping for SOC major groups
const iconMapping = {
  "11": PiBriefcaseLight,
  "13": PiCurrencyDollarLight,
  "15": PiLaptopLight,
  "17": PiRulerLight,
  "19": PiAtomLight,
  "21": PiHandshakeLight,
  "23": PiScalesLight,
  "25": PiBookOpenLight,
  "27": PiMaskHappyLight,
  "29": PiStethoscopeLight,
  "31": PiHeartbeatLight,
  "33": PiShieldCheckLight,
  "35": PiChefHatLight,
  "37": PiBroomLight,
  "39": PiHandHeartLight,
  "41": PiShoppingCartLight,
  "43": PiOfficeChairLight,
  "45": PiPlantLight,
  "47": PiHardHatLight,
  "49": PiWrenchLight,
  "51": PiFactoryLight,
  "53": PiTruckLight,
  "55": PiTargetLight,
}

// Short names for display
const shortMajorMapping = {
  "11": "Management",
  "13": "Business/Finance",
  "15": "Computer/Math",
  "17": "Architecture/Eng",
  "19": "Sciences",
  "21": "Social Service",
  "23": "Legal",
  "25": "Education",
  "27": "Arts/Media",
  "29": "Healthcare",
  "31": "Healthcare Support",
  "33": "Protective Service",
  "35": "Food Service",
  "37": "Building/Grounds",
  "39": "Personal Service",
  "41": "Sales",
  "43": "Office/Admin",
  "45": "Farming/Forestry",
  "47": "Construction",
  "49": "Installation/Repair",
  "51": "Production",
  "53": "Transportation",
  "55": "Military",
}

export default function LaborMarketTable({
  jobs = [],
  socCodes = [],
  title,
  subtitle,
  showExport = true
}) {
  const [copied, setCopied] = useState(false)
  const [exported, setExported] = useState(false)

  // Get global jobs data from context
  const { jobs: contextJobs } = useContext(ProductsContext)

  // Debug logging
  // Resolve jobs: use provided jobs array, or filter from context using socCodes
  const resolvedJobs = useMemo(() => {
    // If jobs array is provided, use it directly
    if (jobs && jobs.length > 0) {
      return jobs
    }

    // If socCodes provided, filter from context
    if (socCodes && socCodes.length > 0 && contextJobs?.socData) {
      const filtered = contextJobs.socData.filter(job =>
        socCodes.includes(job.onetSOC)
      )
      return filtered
    }

    return []
  }, [jobs, socCodes, contextJobs])

  // Group jobs by major occupation code
  const groupedJobs = useMemo(() => {
    const groups = {}

    resolvedJobs.forEach(job => {
      const majorCode = job.onetSOC?.substring(0, 2) || '99'
      if (!groups[majorCode]) {
        groups[majorCode] = []
      }
      groups[majorCode].push(job)
    })

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [resolvedJobs])

  // Format SOC code
  const formatSOC = (soc) => {
    if (!soc) return 'N/A'
    return soc.replace(/(\d{2})(\d{4})/, '$1-$2')
  }

  // Copy to clipboard
  const copyToClipboard = async () => {
    const headers = ['SOC', 'Occupation', 'Education', 'Annual Salary', 'Hourly Wage', 'Openings', 'Growth', 'Employment']
    const rows = resolvedJobs.map(job => [
      formatSOC(job.onetSOC),
      job.onetTitle || '',
      job.education || '',
      job.avgAnnualWage ? `$${job.avgAnnualWage.toLocaleString()}` : '',
      job.avgHourlyWage ? `$${job.avgHourlyWage}` : '',
      job.avgTotalOpenings?.toLocaleString() || '',
      job.longtermGrowth ? `${(job.longtermGrowth * 100).toFixed(2)}%` : '',
      job.estimatedEmployment?.toLocaleString() || ''
    ])

    const text = [headers, ...rows]
      .map(row => row.join('\t'))
      .join('\n')

    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['SOC', 'Occupation', 'Education', 'Annual Salary', 'Hourly Wage', 'Openings', 'Growth %', 'Employment']
    const rows = resolvedJobs.map(job => [
      formatSOC(job.onetSOC),
      job.onetTitle || '',
      job.education || '',
      job.avgAnnualWage || '',
      job.avgHourlyWage || '',
      job.avgTotalOpenings || '',
      job.longtermGrowth ? (job.longtermGrowth * 100).toFixed(2) : '',
      job.estimatedEmployment || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell =>
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'labor-market-data.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setExported(true)
    setTimeout(() => setExported(false), 2000)
  }

  if (!resolvedJobs.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No job data available for this program.
        <br />
        <span className="text-xs">Labor market data may not be mapped for this CIP code.</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {(title || showExport) && (
        <div className="flex justify-between items-start px-4 pt-2">
          {title && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              )}
            </div>
          )}

          {showExport && (
            <div className="flex gap-2">
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {exported ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    Exported!
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-100 text-slate-700 uppercase text-xs">
              <tr>
                <th className="font-semibold px-3 py-3 text-left w-20">SOC</th>
                <th className="font-semibold px-3 py-3 text-left" style={{ minWidth: '320px' }}>Occupation</th>
                <th className="font-semibold px-3 py-3 text-left w-24">Avg. Salary</th>
                <th className="font-semibold px-3 py-3 text-left w-20">Openings</th>
                <th className="font-semibold px-3 py-3 text-left w-20">Growth</th>
                <th className="font-semibold px-3 py-3 text-left w-24">Employment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupedJobs.map(([majorCode, groupJobs]) => {
                const Icon = iconMapping[majorCode]

                return (
                  <React.Fragment key={majorCode}>
                    {/* Major Group Header Row */}
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
                          <span className="text-xs font-medium text-gray-700 uppercase">
                            {shortMajorMapping[majorCode] || 'Other'}
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Jobs in this group */}
                    {groupJobs.map((job, index) => {
                      return (
                        <tr key={`${job.onetSOC}-${index}`} className="hover:bg-gray-50 transition-colors">
                          {/* SOC Code */}
                          <td className="px-3 py-3 align-top">
                            <span className="text-[10px] font-mono text-zinc-400 whitespace-nowrap">
                              {formatSOC(job.onetSOC)}
                            </span>
                          </td>

                          {/* Occupation with circular demand rating on left */}
                          <td className="px-3 py-3" style={{ minWidth: '320px' }}>
                            <div className="flex items-start gap-3">
                              <DemandHoverTooltip
                                onetSOC={job.onetSOC}
                              >
                                <div className="cursor-pointer">
                                  <CircularRating
                                    demand={job.maxDemand}
                                    size="md"
                                    className="pointer-events-none"
                                  />
                                </div>
                              </DemandHoverTooltip>
                              <div>
                                <div className="text-base font-semibold text-gray-900">
                                  {job.onetTitle}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {job.education || 'Education not specified'}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Salary */}
                          <td className="px-3 py-3">
                            <div className="w-20">
                              <div className="text-sm font-medium text-gray-700">
                                ${job.avgAnnualWage?.toLocaleString() || 'N/A'}
                              </div>
                              {job.wageRating != null && (
                                <div className="h-2 flex mt-1">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <div
                                      key={i}
                                      className={`w-full h-2 border border-white ${
                                        job.wageRating > i ? 'bg-emerald-300' : 'bg-zinc-100'
                                      } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Openings */}
                          <td className="px-3 py-3">
                            <div className="w-16">
                              <div className="text-sm font-medium text-gray-700">
                                {job.avgTotalOpenings?.toLocaleString() || 'N/A'}
                              </div>
                              {job.totalOpeningsRating != null && (
                                <div className="h-2 flex mt-1">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <div
                                      key={i}
                                      className={`w-full h-2 border border-white ${
                                        job.totalOpeningsRating > i ? 'bg-sky-300' : 'bg-zinc-100'
                                      } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Growth */}
                          <td className="px-3 py-3">
                            <div className="w-16">
                              <div className="text-sm font-medium text-gray-700">
                                {job.longtermGrowth != null
                                  ? `${(job.longtermGrowth * 100).toFixed(1)}%`
                                  : 'N/A'}
                              </div>
                              {job.growthRating != null && (
                                <div className="h-2 flex mt-1">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <div
                                      key={i}
                                      className={`w-full h-2 border border-white ${
                                        job.growthRating > i ? 'bg-sky-300' : 'bg-zinc-100'
                                      } ${i === 0 ? 'rounded-l' : i === 4 ? 'rounded-r' : ''}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Employment */}
                          <td className="px-3 py-3">
                            <div className="text-sm font-medium text-gray-700">
                              {job.estimatedEmployment?.toLocaleString() || 'N/A'}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
